import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { errText, parsePermissions } from './ftpViewHelpers';

/* ── Permissions (chmod) modal — SFTP only ──────────────────────────────── */

export const PERM_ROWS = [
  { id: 'owner', labelKey: 'ftp.permOwner', shift: 6 },
  { id: 'group', labelKey: 'ftp.permGroup', shift: 3 },
  { id: 'others', labelKey: 'ftp.permOthers', shift: 0 },
];

export const PERM_COLS = [
  { id: 'read', labelKey: 'ftp.permRead', bit: 4 },
  { id: 'write', labelKey: 'ftp.permWrite', bit: 2 },
  { id: 'exec', labelKey: 'ftp.permExec', bit: 1 },
];

export function PermModal({ entry, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(() => parsePermissions(entry.permissions, entry.isDir));
  const [octalDraft, setOctalDraft] = useState(() =>
    parsePermissions(entry.permissions, entry.isDir).toString(8).padStart(3, '0')
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const applyMode = (next) => {
    const clamped = next & 0o777;
    setMode(clamped);
    setOctalDraft(clamped.toString(8).padStart(3, '0'));
  };

  const onOctalChange = (raw) => {
    if (!/^[0-7]{0,4}$/.test(raw)) return;
    setOctalDraft(raw);
    if (/^[0-7]{3,4}$/.test(raw)) setMode(parseInt(raw, 8) & 0o777);
  };

  const toggleBit = (shift, bit) => applyMode(mode ^ (bit << shift));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(mode);
      onClose();
    } catch (err) {
      setError(errText(err));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={t('ftp.permissionsTitle')} subtitle={entry.name}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="ftp-perm-grid">
          <div className="ftp-perm-grid-head">
            <span />
            {PERM_COLS.map((col) => (
              <span key={col.id} className="ftp-perm-col-label">
                {t(col.labelKey)}
              </span>
            ))}
          </div>
          {PERM_ROWS.map((row) => (
            <div key={row.id} className="ftp-perm-grid-row">
              <span className="ftp-perm-row-label">{t(row.labelKey)}</span>
              {PERM_COLS.map((col) => (
                <label key={col.id} className="ftp-perm-check">
                  <input
                    type="checkbox"
                    className="checkbox-brand"
                    checked={((mode >> row.shift) & col.bit) !== 0}
                    onChange={() => toggleBit(row.shift, col.bit)}
                  />
                </label>
              ))}
            </div>
          ))}
        </div>
        <div>
          <label className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
            {t('ftp.octalLabel')}
          </label>
          <input
            type="text"
            className="modal-input"
            style={{ width: 120, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
            value={octalDraft}
            onChange={(e) => onOctalChange(e.target.value)}
            spellCheck={false}
          />
        </div>
        {error && <div className="ftp-modal-error">{error}</div>}
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {t('ftp.apply')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
